// The page "Verlauf" (concept beans-and-shot-history §3, §4 E and F; FR-27 "nach Bohne und Zeitraum"): filters by
// bean, period and taste, the key figures of the filter, the shots grouped by day with bean and "Tag n", and the
// comparison of 2-3 shots (curves over each other, figures side by side, differences marked).
import {
  tasteTagCatalog,
  type BeanItem,
  type ShotItem,
  type ShotSummaryResponse,
} from '@brewcore/app-contract';
import type { BeansView } from '../beans.js';
import { typeset } from '../i18n.js';
import { ratioOf } from '../recipe.js';
import { localDayOf } from '../schedule.js';
import type { Period, ShotFilter, ShotHistoryView, TasteFilter } from '../shot-history.js';
import { shotLine, type ShotLineView } from './brew.js';
import { beanLabel, dayAfterRoast } from './beans.js';
import type { ScreenContext } from './context.js';
import { curveData, type CurveData } from './curves.js';
import {
  deviation,
  dotJoin,
  formatNumber,
  metaOf,
  offTarget,
  shotMs,
  shotTime,
  signed,
  stopModeText,
  whenText,
} from './format.js';

/** Most shots compared at once. */
export const COMPARE_MAX = 3;

export interface HistoryFilterView {
  bean: { label: string; value: string; options: { value: string; label: string }[] };
  period: { label: string; options: { value: Period; label: string; on: boolean }[] };
  taste: { label: string; options: { value: TasteFilter; label: string; on: boolean }[] };
  /** a taste tag of the catalog, '' = all */
  tag: { label: string; value: string; options: { value: string; label: string }[] };
}

/** The bean choice lists the open beans, then the archived ones, then "ohne Bohne". */
export function historyFilterView(
  ctx: ScreenContext,
  filter: ShotFilter,
  beans: BeansView,
): HistoryFilterView {
  const open = beans.items.filter((b) => !b.archivedAt);
  const archived = beans.items.filter((b) => b.archivedAt);
  const periods: [Period, string][] = [
    ['today', ctx.tr('periodToday')],
    ['7d', ctx.tr('period7')],
    ['30d', ctx.tr('period30')],
    ['all', ctx.tr('periodAll')],
  ];
  const tastes: [TasteFilter, string][] = [
    ['all', ctx.tr('tasteAll')],
    ['sour', ctx.tr('sour')],
    ['good', ctx.tr('good')],
    ['bitter', ctx.tr('bitter')],
    ['unrated', ctx.tr('unrated')],
  ];
  return {
    bean: {
      label: ctx.tr('beans'),
      value: filter.bean ?? '',
      options: [
        { value: '', label: ctx.tr('allBeans') },
        ...open.map((b) => ({ value: b.id, label: beanLabel(b) })),
        ...archived.map((b) => ({ value: b.id, label: `${beanLabel(b)} (${ctx.tr('beanArchived')})` })),
        { value: 'none', label: ctx.tr('withoutBean') },
      ],
    },
    period: {
      label: ctx.tr('period'),
      options: periods.map(([value, label]) => ({ value, label, on: filter.period === value })),
    },
    taste: {
      label: ctx.tr('tasteQ'),
      options: tastes.map(([value, label]) => ({ value, label, on: filter.taste === value })),
    },
    tag: {
      label: ctx.tr('tasteTags'),
      value: filter.tag ?? '',
      options: [
        { value: '', label: ctx.tr('tagsAll') },
        ...tasteTagCatalog.tags.map((t) => ({ value: t.id, label: ctx.lang === 'de' ? t.de : t.en })),
      ],
    },
  };
}

/** "41 Bezüge · Ø 27,3 s · Ø 36,2 g · Ø 1:2,0"; null until the figures came. */
export function historySummary(ctx: ScreenContext, s: ShotSummaryResponse | null): string | null {
  if (!s) return null;
  if (!s.count) return ctx.tr('historyNone');
  const n = (v: number, d: number) => formatNumber(v, d, ctx.lang);
  return [
    ctx.tr(s.count === 1 ? 'beanShotOne' : 'beanShots', { n: s.count }),
    s.time !== null ? `Ø ${n(s.time, 1)} s` : '',
    s.weight !== null ? `Ø ${n(s.weight, 1)} g` : '',
    s.ratio !== null ? `Ø 1:${n(s.ratio, 1)}` : '',
  ]
    .filter(Boolean)
    .join(' · ');
}

export interface HistoryStatsView {
  /** "42" and "Bezüge" (prototype "Verlauf": the number big, the word beside it) */
  count: string;
  countWord: string;
  /** "8 mehr als in den 30 Tagen davor"; null without the period before */
  compare: string | null;
  facts: { key: string; label: string; value: string }[];
  /** the app's reading of the list: how many missed their target */
  verdict: string | null;
}

/**
 * The figures of the filter as a card (prototype "Verlauf"): how many, compared with the period before, the averages
 * and how many tasted good with their names above, and one line on how many missed their target.
 */
export function historyStats(
  ctx: ScreenContext,
  s: ShotSummaryResponse | null,
  extra: {
    good?: number | null;
    before?: number | null;
    period?: Period;
    items?: readonly ShotItem[];
    zone?: string;
  } = {},
): HistoryStatsView | null {
  if (!s || !s.count) return null;
  const n = (v: number, d: number) => formatNumber(v, d, ctx.lang);
  const facts: { key: string; label: string; value: string }[] = [];
  if (s.time !== null) facts.push({ key: 'time', label: ctx.tr('avgTime'), value: `${n(s.time, 0)}\u00a0s` });
  if (s.weight !== null)
    facts.push({ key: 'weight', label: ctx.tr('avgWeight'), value: `${n(s.weight, 1)}\u00a0g` });
  if (typeof extra.good === 'number')
    facts.push({ key: 'good', label: ctx.tr('good'), value: ctx.tr('nOfM', { n: extra.good, m: s.count }) });
  else if (s.ratio !== null)
    facts.push({ key: 'ratio', label: ctx.tr('avgRatio'), value: `1:${n(s.ratio, 1)}` });
  const when =
    extra.period === 'today'
      ? ctx.tr('beforeToday')
      : extra.period === '7d'
        ? ctx.tr('beforeWeek')
        : extra.period === '30d'
          ? ctx.tr('before30')
          : extra.period === 'month'
            ? ctx.tr('beforeMonth', { month: monthBefore(ctx, extra.zone) })
            : null;
  const diff = typeof extra.before === 'number' ? s.count - extra.before : null;
  const compare =
    when === null || diff === null
      ? null
      : diff > 0
        ? ctx.tr('moreThanBefore', { n: diff, when })
        : diff < 0
          ? ctx.tr('lessThanBefore', { n: -diff, when })
          : ctx.tr('sameAsBefore', { when });
  return {
    count: String(s.count),
    countWord: ctx.tr(s.count === 1 ? 'shotOne' : 'shotsWord'),
    compare,
    facts,
    verdict: historyVerdict(ctx, extra.items ?? []),
  };
}

/** "September": the month before today's, in the app's language (the calendar's figures compare with it). */
function monthBefore(ctx: ScreenContext, zone?: string): string {
  const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
  const parts = new Intl.DateTimeFormat(loc, {
    year: 'numeric',
    month: 'numeric',
    timeZone: zone,
  }).formatToParts(new Date(ctx.now));
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  return new Intl.DateTimeFormat(loc, { month: 'long', timeZone: 'UTC' }).format(
    new Date(Date.UTC(num('year'), num('month') - 2, 15)),
  );
}

/** How many of the listed shots with a target missed it by more than 1 g or 1 s (prototype "Verlauf"). */
export function historyVerdict(ctx: ScreenContext, items: readonly ShotItem[]): string | null {
  const devs = items.map((s) => deviation(metaOf(s))).filter((d): d is NonNullable<typeof d> => d !== null);
  if (devs.length < 3) return null;
  const off = devs.filter((d) => offTarget(d)).length;
  if (off === 0) return ctx.tr('verdictAll');
  if (off === 1) return ctx.tr('verdictMostOne');
  if (off * 2 < devs.length)
    return ctx.tr('verdictMost', { n: off <= 5 ? ctx.tr(`w${off}` as 'w2') : String(off) });
  return ctx.tr('verdictMany', { n: off, m: devs.length });
}

export interface CalendarView {
  title: string;
  weekdays: string[];
  /**
   * the weeks of the month from Monday, a day null outside it; key = the local day as in HistoryGroup.day (null
   * outside the month); dots = shots that day; later = after today (nothing to show yet)
   */
  cells: { day: number | null; key: number | null; dots: number; today: boolean; later: boolean }[];
  /** today's local day: chosen when the calendar opens */
  today: number;
  /** the listed shots reach back to the 1st of the month; until then the page loads more for the dots */
  covered: boolean;
}

/**
 * The month of today with a dot for every listed shot of a day (prototype "Verlauf", view "Kalender"); a tap on a day
 * shows that day's shots below it (Dominik 21.10.2026: "der kalender … ist nicht anklickbar").
 */
export function historyCalendar(ctx: ScreenContext, items: readonly ShotItem[], zone?: string): CalendarView {
  const loc = ctx.lang === 'de' ? 'de-DE' : 'en-GB';
  const now = new Date(ctx.now);
  const fmt = (o: Intl.DateTimeFormatOptions) => new Intl.DateTimeFormat(loc, { timeZone: zone, ...o });
  const parts = fmt({ year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(now);
  const num = (type: string) => Number(parts.find((p) => p.type === type)?.value ?? 0);
  const year = num('year');
  const month = num('month');
  const today = num('day');
  const first = new Date(Date.UTC(year, month - 1, 1));
  const length = new Date(Date.UTC(year, month, 0)).getUTCDate();
  const lead = (first.getUTCDay() + 6) % 7;
  const counts = new Map<number, number>();
  for (const s of items) {
    const p = fmt({ year: 'numeric', month: 'numeric', day: 'numeric' }).formatToParts(new Date(shotMs(s)));
    const y = Number(p.find((x) => x.type === 'year')?.value);
    const m = Number(p.find((x) => x.type === 'month')?.value);
    const d = Number(p.find((x) => x.type === 'day')?.value);
    if (y === year && m === month) counts.set(d, (counts.get(d) ?? 0) + 1);
  }
  const keyOf = (d: number) => Math.round(Date.UTC(year, month - 1, d) / 86_400_000);
  const none = { day: null, key: null, dots: 0, today: false, later: false };
  const cells: CalendarView['cells'] = [];
  for (let i = 0; i < lead; i++) cells.push(none);
  for (let d = 1; d <= length; d++)
    cells.push({ day: d, key: keyOf(d), dots: counts.get(d) ?? 0, today: d === today, later: d > today });
  while (cells.length % 7) cells.push(none);
  const oldest = items.at(-1);
  const covered = !oldest || localDayOf(shotMs(oldest), zone) < keyOf(1);
  // Monday 2026-10-19 and the six days after it, in the app's language
  const weekdays = Array.from({ length: 7 }, (_, i) =>
    new Intl.DateTimeFormat(loc, { weekday: 'short', timeZone: 'UTC' })
      .format(new Date(Date.UTC(2026, 9, 19 + i)))
      .replace('.', ''),
  );
  return {
    title: fmt({ month: 'long', year: 'numeric' }).format(now),
    weekdays,
    cells,
    today: keyOf(today),
    covered,
  };
}

export interface HistoryLine extends ShotLineView {
  /** time of day only (the day is the group's heading) */
  clock: string;
  /** "Hayb Guji · Tag 9" */
  bean: string | null;
}

export interface HistoryGroup {
  day: number;
  title: string;
  items: HistoryLine[];
}

const beanName = (beans: BeansView, shot: ShotItem): string | null =>
  beans.items.find((b) => b.id === shot.beanId)?.name ?? metaOf(shot).bean?.name ?? null;

/** "Hayb Guji · The Barn · Tag 9": the bean with its roaster and the day after roasting on the day of the shot. */
export function shotBeanText(
  ctx: ScreenContext,
  shot: ShotItem,
  beans: BeansView,
  zone?: string,
): string | null {
  const known = beans.items.find((b) => b.id === shot.beanId);
  const name = known ? beanLabel(known) : beanName(beans, shot);
  const day = dayAfterRoast(shot.roastDay, localDayOf(shotMs(shot), zone));
  return dotJoin([name, day !== null ? ctx.tr('dayN', { n: day }) : '']) || null;
}

/**
 * The same as parts for a line that never wraps (FitLine): the bean, the day after roasting, the roaster last, so it is
 * the first to drop out where the line is short (23.10.2026: "… · The Barn · Tag / 11" broke under a shot's title).
 */
export function shotBeanParts(ctx: ScreenContext, shot: ShotItem, beans: BeansView, zone?: string): string[] {
  const known = beans.items.find((b) => b.id === shot.beanId);
  const name = known ? known.name : beanName(beans, shot);
  const day = dayAfterRoast(shot.roastDay, localDayOf(shotMs(shot), zone));
  return [name ?? '', day !== null ? ctx.tr('dayN', { n: day }) : '', known?.roaster?.trim() ?? ''].filter(
    Boolean,
  );
}

/** The shots grouped by their local day (the browser's zone, FR-17), newest first. */
export function historyGroups(
  ctx: ScreenContext,
  items: readonly ShotItem[],
  beans: BeansView,
  zone?: string,
): HistoryGroup[] {
  const today = localDayOf(ctx.now, zone);
  const groups: HistoryGroup[] = [];
  for (const s of items) {
    const ms = shotMs(s);
    const day = localDayOf(ms, zone);
    let g = groups.at(-1);
    if (!g || g.day !== day) {
      const title =
        day === today
          ? ctx.tr('today')
          : day === today - 1
            ? ctx.tr('yesterday')
            : new Date(ms).toLocaleDateString(ctx.lang === 'de' ? 'de-DE' : 'en-GB', {
                weekday: 'long',
                day: '2-digit',
                month: '2-digit',
                timeZone: zone,
              });
      // a heading starts with a capital ("Heute", "Gestern", "Sonntag, 18.10."), like the prototype's
      g = { day, title: title.charAt(0).toUpperCase() + title.slice(1), items: [] };
      groups.push(g);
    }
    g.items.push({
      ...shotLine(ctx, s),
      clock: new Date(ms).toLocaleTimeString(ctx.lang === 'de' ? 'de-DE' : 'en-GB', {
        hour: '2-digit',
        minute: '2-digit',
        timeZone: zone,
      }),
      bean: shotBeanText(ctx, s, beans, zone),
    });
  }
  return groups;
}

export interface HistoryPageView {
  title: string;
  filter: HistoryFilterView;
  summary: string | null;
  /** the same as figures for the card over the list */
  stats: ReturnType<typeof historyStats>;
  groups: HistoryGroup[];
  loading: boolean;
  empty: string | null;
  error: string | null;
  more: { label: string; enabled: boolean } | null;
}

export function historyPage(
  ctx: ScreenContext,
  hv: ShotHistoryView,
  beans: BeansView,
  zone?: string,
): HistoryPageView {
  const empty = hv.loaded && hv.items.length === 0 ? ctx.tr('historyNone') : null;
  return {
    title: ctx.tr('shotHistory'),
    filter: historyFilterView(ctx, hv.filter, beans),
    // nothing found: said once, not as figures and again below them
    summary: empty ? null : historySummary(ctx, hv.summary),
    stats: empty
      ? null
      : historyStats(ctx, hv.summary, {
          good: hv.good,
          before: hv.before,
          period: hv.filter.period,
          items: hv.items,
          zone,
        }),
    groups: historyGroups(ctx, hv.items, beans, zone),
    loading: !hv.loaded && hv.loading,
    empty,
    error: hv.error,
    more: hv.next ? { label: ctx.tr('loadMore'), enabled: !hv.loading } : null,
  };
}

/** A tap on a shot's box while comparing: on or off, at most three. */
export function toggleCompare(chosen: readonly string[], id: string): string[] {
  if (chosen.includes(id)) return chosen.filter((x) => x !== id);
  return chosen.length >= COMPARE_MAX ? [...chosen] : [...chosen, id];
}

export interface CompareBarView {
  text: string;
  enabled: boolean;
  label: string;
}

export function compareBar(ctx: ScreenContext, chosen: readonly string[]): CompareBarView {
  return {
    text: chosen.length ? ctx.tr('compareChosen', { n: chosen.length }) : ctx.tr('compareHint'),
    enabled: chosen.length >= 2,
    label: ctx.tr('compare'),
  };
}

// ------------------------------------------------------------------------------------------------ comparison

export type CompareKind = 'weight' | 'flow' | 'temp';

export interface CompareView {
  title: string;
  kinds: { value: CompareKind; label: string; on: boolean }[];
  kind: CompareKind;
  /** one per shot in the order chosen; curve undefined while it loads, null without that channel */
  series: { id: string; label: string; curve: (number | null)[] | null | undefined; dt: number }[];
  durationMs: number;
  /**
   * one scale for all curves (weight and flow from 0, temperature on its own range) and its labels: the top and the
   * bottom edge on the right, the end of the time axis (review 19.10.2026: the curves had no numbers at all)
   */
  scale: { min: number; max: number; top: string; bottom: string; end: string };
  /** figures side by side; diff = differs from the first shot; a row no shot has a value for is left out */
  rows: { label: string; cells: { text: string; diff: boolean }[] }[];
  missing: string[];
}

/** The shots side by side; `kind` null = weight if any shot has it, else temperature. */
export function compareView(
  ctx: ScreenContext,
  ids: readonly string[],
  shots: Readonly<Record<string, ShotItem>>,
  beans: BeansView,
  kind: CompareKind | null,
  zone?: string,
): CompareView {
  const list = ids.map((id) => shots[id]);
  const curves: (CurveData | null | undefined)[] = list.map((s) => (s ? curveData(s.curve) : undefined));
  const hasWeight = curves.some((c) => c?.weight);
  const chosen: CompareKind = kind ?? (hasWeight ? 'weight' : 'temp');
  const pick = (c: CurveData | null | undefined) =>
    c === undefined ? undefined : c ? (c[chosen] ?? null) : null;
  const n = (v: number, d: number) => formatNumber(v, d, ctx.lang);
  const cell = (s: ShotItem | undefined, f: (s: ShotItem) => string) => (s ? typeset(f(s)) : '…');
  const rowOf = (label: string, f: (s: ShotItem) => string) => {
    const texts = list.map((s) => cell(s, f));
    return {
      label,
      cells: texts.map((text, i) => ({ text, diff: i > 0 && text !== texts[0] && text !== '…' })),
    };
  };
  const meta = (s: ShotItem) => metaOf(s);
  const durationMs = Math.max(
    20_000,
    ...curves.map((c) =>
      c ? Math.max(c.weight?.length ?? 0, c.flow?.length ?? 0, c.temp?.length ?? 0) * c.dt : 0,
    ),
  );
  // the scale CompareCurve draws on: a little room above and below, weight and flow from 0
  const values = ids
    .map((_, i) => pick(curves[i]))
    .flatMap((c) => (c ? c.filter((v): v is number => v !== null) : []));
  const lo = values.length ? Math.min(...values) : 0;
  const hi = values.length ? Math.max(...values) : 1;
  const pad = (hi - lo) * 0.06 || 1;
  const min = lo > 20 ? lo - pad : 0;
  const max = hi + pad;
  const unit = chosen === 'weight' ? 'g' : chosen === 'flow' ? 'g/s' : '°C';
  const edge = (v: number) => typeset(`${n(v, chosen === 'weight' ? 0 : 1)} ${unit}`);
  return {
    title: ctx.tr('compareTitle'),
    kinds: (
      [
        ['weight', ctx.tr('shotWeight')],
        ['flow', ctx.tr('flowLine')],
        ['temp', ctx.tr('tempLine')],
      ] as [CompareKind, string][]
    ).map(([value, label]) => ({ value, label, on: value === chosen })),
    kind: chosen,
    series: ids.map((id, i) => ({
      id,
      label: list[i] ? whenText(shotMs(list[i]), ctx.lang, ctx.now) : '…',
      curve: pick(curves[i]),
      dt: curves[i]?.dt ?? 1000,
    })),
    durationMs,
    scale: {
      min,
      max,
      top: edge(max),
      bottom: edge(min),
      end: typeset(`${Math.round(durationMs / 1000)} s`),
    },
    rows: [
      rowOf(ctx.tr('shotTime'), (s) => {
        const t = shotTime(meta(s));
        return `${n(t.main, 1)} s${t.pi ? ` +${n(t.pi, 0)}` : ''}`;
      }),
      rowOf(ctx.tr('shotWeight'), (s) =>
        typeof meta(s).weight === 'number' ? `${n(meta(s).weight as number, 1)} g` : '–',
      ),
      rowOf(ctx.tr('dose'), (s) => (meta(s).dose ? `${n(meta(s).dose as number, 1)} g` : '–')),
      rowOf(ctx.tr('ratio'), (s) => {
        const m = meta(s);
        const r = m.dose && typeof m.weight === 'number' ? ratioOf(m.dose, m.weight) : null;
        return r ? `1:${n(r, 1)}` : '–';
      }),
      rowOf(ctx.tr('grindLabel'), (s) => meta(s).grind || '–'),
      rowOf(ctx.tr('beans'), (s) => beanName(beans, s) ?? '–'),
      rowOf(ctx.tr('dayLabel'), (s) => {
        const d = dayAfterRoast(s.roastDay, localDayOf(shotMs(s), zone));
        return d !== null ? String(d) : '–';
      }),
      rowOf(ctx.tr('shotSetpoint'), (s) => {
        const m = meta(s);
        const profile =
          typeof m.tempEnd === 'number' && m.tempEnd !== 0 ? ` ${signed(m.tempEnd, ctx.lang)} K` : '';
        return typeof m.setpoint === 'number' ? `${n(m.setpoint, 1)} °C${profile}` : '–';
      }),
      rowOf(ctx.tr('startTemp'), (s) =>
        typeof meta(s).startTemp === 'number' ? `${n(meta(s).startTemp as number, 1)} °C` : '–',
      ),
      // the target says how it stopped ("36,0 g" by weight, "28 s" by time): one line in a narrow column
      rowOf(ctx.tr('shotStop'), (s) => {
        const m = meta(s);
        if (m.stopMode === 'weight' && m.targetWeight) return `${n(m.targetWeight, 1)} g`;
        if (m.stopMode === 'time' && m.targetTime) return `${n(m.targetTime, 0)} s`;
        return stopModeText(m, ctx.lang) || '–';
      }),
      rowOf(ctx.tr('firstDropAt'), (s) =>
        typeof meta(s).firstDrop === 'number' ? `${n(meta(s).firstDrop as number, 1)} s` : '–',
      ),
      rowOf(ctx.tr('tasteQ'), (s) =>
        s.rating === 1
          ? ctx.tr('sour')
          : s.rating === 2
            ? ctx.tr('good')
            : s.rating === 3
              ? ctx.tr('bitter')
              : '–',
      ),
    ].filter((r) => r.cells.some((c) => c.text !== '–')),
    missing: ids.filter((id) => !shots[id]),
  };
}

/** Beans for the filter, also in tests without a store. */
export const beansViewOf = (items: BeanItem[]): BeansView => ({
  items,
  loaded: true,
  loading: false,
  details: {},
  error: null,
});
